import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Space,
  Divider,
  Card,
  Avatar,
  Row,
  Col,
  Statistic,
  Typography,
  Descriptions,
  Timeline,
  Tooltip,
  Tabs,
  Badge
} from "antd";
import {
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  UserOutlined,
  GlobalOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  TeamOutlined,
  NumberOutlined,
  HomeOutlined
} from "@ant-design/icons";
import API from "../../API/API";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const YardDetailsPage = () => {
  const { id } = useParams();
  const [yard, setYard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [form] = Form.useForm();

const fetchYard = async () => {
  try {
    const res = await API.GetYardById(id);
    if (res?.status) {
      const yardData = res.data;
      setYard(yardData);
      
      // Create contacts array with extensions - even if arrays are empty
      const contacts = [];
      const maxLength = Math.max(
        yardData.contactPerson?.length || 0,
        yardData.email?.length || 0,
        yardData.contactNumbers?.length || 0,
        yardData.contactPersonExtension?.length || 0  
      );
      
      // If all arrays are empty, create one empty contact
      if (maxLength === 0) {
        contacts.push({ name: '', email: '', phone: '', extension: '' }); // ADD extension
      } else {
        for (let i = 0; i < maxLength; i++) {
          contacts.push({
            name: yardData.contactPerson?.[i] || '',
            email: yardData.email?.[i] || '',
            phone: yardData.contactNumbers?.[i] || '',
            extension: yardData.contactPersonExtension?.[i] || ''  
          });
        }
      }

      form.setFieldsValue({
        yardName: yardData.yardName,
        contacts: contacts,
        link: yardData.link,
        yardAddress: yardData.yardAddress,
      });
    }
  } catch (err) {
    console.error("Failed to fetch yard", err);
    message.error("Failed to load yard details");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchYard();
  }, [id]);

const handleEditSubmit = async ({ confirmDuplicates = false } = {}) => {
  try {
    const values = await form.validateFields();

    // DON'T filter out empty values - send them as-is
    const contactPersons = values.contacts?.map(c => c.name?.trim()) || [];
    const emails = values.contacts?.map(c => c.email?.trim().toLowerCase()) || [];
    const contactNumbers = values.contacts?.map(c => c.phone?.trim()) || [];
    const contactPersonExtensions = values.contacts?.map(c => c.extension?.trim()) || [];

    const payload = {
      yardName: values.yardName,
      contactPerson: contactPersons,
      email: emails,
      contactNumbers: contactNumbers,
      contactPersonExtension: contactPersonExtensions,
      link: values.link,
      yardAddress: values.yardAddress,
    };

    if (confirmDuplicates) {
      payload.confirmDuplicates = true;
    }

    console.log("Sending payload:", payload); // Debug log

    const response = await API.UpdateYard(id, payload);
    if (response.success) {
      message.success("Yard updated successfully");
      setIsModalOpen(false);
      fetchYard();
      return;
    }

    // Name/email/phone already used at another yard — warn instead of
    // blocking, since the same contractor often works with multiple yards.
    if (response.requiresConfirmation && Array.isArray(response.duplicates) && response.duplicates.length > 0) {
      Modal.confirm({
        title: "Contact already exists at another yard",
        content: (
          <div>
            {response.duplicates.map((dup, idx) => (
              <p key={idx} style={{ marginBottom: 4 }}>
                {dup.field === "name" ? (
                  <>Person <strong>{dup.value}</strong> already exists in <strong>{dup.yardName}</strong>.</>
                ) : (
                  <>This {dup.field === "email" ? "email" : "phone number"} (<strong>{dup.value}</strong>) is already
                  associated with <strong>{dup.yardName}</strong>.</>
                )}
              </p>
            ))}
            <p style={{ marginBottom: 0 }}>Add anyway?</p>
          </div>
        ),
        okText: "Add Anyway",
        cancelText: "Cancel",
        onOk: () => handleEditSubmit({ confirmDuplicates: true }),
      });
      return;
    }

    message.error(response.message || "Update failed");
  } catch (err) {
    console.error("Validation error:", err);
    message.error("Please fill all required fields");
  }
};

  const formatPhoneNumber = (num) => {
    if (!num) return "";
    const cleaned = num.replace(/\D/g, "");
    const number = cleaned.length === 11 && cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;

    if (number.length === 10) {
      const area = number.slice(0, 3);
      const mid = number.slice(3, 6);
      const last = number.slice(6, 10);
      return `(${area}) ${mid}-${last}`;
    }

    return num;
  };

  if (loading || !yard) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "100vh",
        background: "#f5f5f5"
      }}>
        <Card style={{ textAlign: "center", padding: 40, borderRadius: 16 }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Text type="secondary">Loading yard details...</Text>
          </div>
        </Card>
      </div>
    );
  }

  const {
    yardName,
    contactPerson = [],
    contactNumbers = [],
    email = [],
    yardAddress,
    createdAt,
    updatedAt,
    link,
    isDeleted,
    _id
  } = yard;

  return (
    <div style={{ 
      padding: 24, 
      background: "#f5f5f5", 
      minHeight: "100vh"
    }}>
      {/* Header Card */}
      <Card 
        style={{ 
          marginBottom: 24, 
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
        }}
        bodyStyle={{ padding: "24px 32px" }}
      >
        <Row align="middle" gutter={[24, 16]}>
          <Col>
            <Avatar 
              size={64} 
              style={{ 
                background: `linear-gradient(135deg, #1677ff 0%, #4096ff 100%)`,
                boxShadow: "0 4px 12px rgba(22,119,255,0.2)"
              }}
              icon={<ShopOutlined style={{ fontSize: 32 }} />}
            />
          </Col>
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Space align="center">
                <Title level={2} style={{ margin: 0, fontWeight: 600 }}>{yardName}</Title>
                {isDeleted && (
                  <Badge count="DELETED" style={{ backgroundColor: '#ff4d4f' }} />
                )}
                <Tag color="cyan" style={{ fontSize: 12 }}>ID: {_id.slice(-8)}</Tag>
              </Space>
              <Space split={<Divider type="vertical" />} size={16}>
                <Text type="secondary">
                  <ClockCircleOutlined /> Created: {new Date(createdAt).toLocaleDateString()}
                </Text>
                <Text type="secondary">
                  <ClockCircleOutlined /> Updated: {new Date(updatedAt).toLocaleDateString()}
                </Text>
              </Space>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                type="primary" 
                size="large"
                icon={<EditOutlined />} 
                onClick={() => setIsModalOpen(true)}
                disabled={isDeleted}
                style={{ borderRadius: 8 }}
              >
                Edit Yard
              </Button>
              <Button 
                size="large"
                icon={<ArrowLeftOutlined />} 
                onClick={() => window.history.back()}
                style={{ borderRadius: 8 }}
              >
                Back
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>



      {/* Main Content Tabs */}
      <Card 
        style={{ 
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          style={{ padding: "0 24px" }}
          tabBarStyle={{ marginBottom: 0 }}
        >
          <TabPane 
            tab={
              <span>
                <InfoCircleOutlined /> Overview
              </span>
            } 
            key="1"
          >
            <div style={{ padding: 24 }}>
              <Row gutter={[24, 24]}>
                {/* Contact Information */}
               <Col span={12}>
  <Card 
    title={
      <Space>
        <UserOutlined style={{ color: "#1677ff" }} />
        <span>Contact Information</span>
      </Space>
    }
    bordered={false}
    style={{ background: "#fafafa", borderRadius: 12 }}
  >
    {contactPerson.map((person, idx) => (
      <Card 
        key={idx}
        size="small"
        style={{ 
          marginBottom: 12,
          borderRadius: 8,
          borderLeft: `4px solid ${idx === 0 ? '#1677ff' : '#52c41a'}`
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space>
            <Badge count={idx + 1} style={{ backgroundColor: idx === 0 ? '#1677ff' : '#52c41a' }} />
            <Text strong>{person}</Text>
            {idx === 0 && <Tag color="blue">Primary</Tag>}
          </Space>
          
          {email[idx] && (
            <div style={{ marginLeft: 28 }}>
              <MailOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
              <a href={`mailto:${email[idx]}`}>{email[idx]}</a>
            </div>
          )}
          
          {contactNumbers[idx] && (
            <div style={{ marginLeft: 28 }}>
              <PhoneOutlined style={{ color: "#52c41a", marginRight: 8 }} />
              <a href={`tel:${contactNumbers[idx]}`}>{formatPhoneNumber(contactNumbers[idx])}</a>
              {/* ADD EXTENSION HERE */}
              {yard.contactPersonExtension?.[idx] && (
                <Tag 
                  color="purple" 
                  style={{ marginLeft: 8, fontSize: 11 }}
                >
                  ext: {yard.contactPersonExtension[idx]}
                </Tag>
              )}
            </div>
          )}
        </Space>
      </Card>
    ))}
  </Card>
</Col>

                {/* Address Information */}
                <Col span={12}>
                  <Card 
                    title={
                      <Space>
                        <EnvironmentOutlined style={{ color: "#ff4d4f" }} />
                        <span>Address Details</span>
                      </Space>
                    }
                    bordered={false}
                    style={{ background: "#fafafa", borderRadius: 12 }}
                  >
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Street Address">
                        {yardAddress?.address || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Street Line 2">
                        {yardAddress?.street || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="City">
                        {yardAddress?.city || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="State">
                        {yardAddress?.state || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Zip Code">
                        {yardAddress?.zip_code || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Country">
                        {yardAddress?.country || "—"}
                      </Descriptions.Item>
                    </Descriptions>

                    {link && (
                      <Card 
                        size="small" 
                        style={{ marginTop: 16, background: "#f0f5ff", borderRadius: 8 }}
                      >
                        <Space>
                          <GlobalOutlined style={{ color: "#1677ff" }} />
                          <Text strong>Website:</Text>
                          <a 
                            href={link.startsWith('http') ? link : `https://${link}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {link}
                          </a>
                        </Space>
                      </Card>
                    )}
                  </Card>
                </Col>
              </Row>
            </div>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <ThunderboltOutlined /> Quick Actions
              </span>
            } 
            key="2"
          >
            <div style={{ padding: 24 }}>
              <Row gutter={[24, 24]}>
            {contactNumbers.map((num, idx) => (
  <Col span={8} key={idx}>
    <Card 
      hoverable
      style={{ 
        borderRadius: 12,
        textAlign: "center",
        background: "linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)"
      }}
      onClick={() => window.open(`tel:${num}`)}
    >
      <PhoneOutlined style={{ fontSize: 32, color: "#52c41a", marginBottom: 12 }} />
      <Title level={4} style={{ margin: "8px 0" }}>Call</Title>
      <Text type="secondary">{contactPerson[idx] || `Phone ${idx + 1}`}</Text>
      <div style={{ marginTop: 8 }}>
        <Tag color="green">{formatPhoneNumber(num)}</Tag>
        {/* ADD EXTENSION HERE */}
        {yard.contactPersonExtension?.[idx] && (
          <Tag color="purple" style={{ marginTop: 4 }}>
            ext: {yard.contactPersonExtension[idx]}
          </Tag>
        )}
      </div>
    </Card>
  </Col>
))}
                {email.map((mail, idx) => (
                  <Col span={8} key={idx}>
                    <Card 
                      hoverable
                      style={{ 
                        borderRadius: 12,
                        textAlign: "center",
                        background: "linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)"
                      }}
                      onClick={() => window.open(`mailto:${mail}`)}
                    >
                      <MailOutlined style={{ fontSize: 32, color: "#fa8c16", marginBottom: 12 }} />
                      <Title level={4} style={{ margin: "8px 0" }}>Email</Title>
                      <Text type="secondary">{contactPerson[idx] || `Contact ${idx + 1}`}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color="orange">{mail}</Tag>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </TabPane>
        </Tabs>
      </Card>
<Modal
  title={
    <Space>
      <Avatar 
        size={40} 
        style={{ background: "#1677ff" }}
        icon={<EditOutlined />}
      />
      <div>
        <Title level={4} style={{ margin: 0 }}>Edit Yard Information</Title>
        <Text type="secondary">{yardName}</Text>
      </div>
    </Space>
  }
  open={isModalOpen}
  onCancel={() => setIsModalOpen(false)}
  onOk={handleEditSubmit}
  okText="Save Changes"
  cancelText="Cancel"
  width={800}
  style={{ top: 20 }}
  okButtonProps={{ size: "large" }}
  cancelButtonProps={{ size: "large" }}
>
  <Divider style={{ margin: "16px 0" }} />
  
  <Form layout="vertical" form={form}>
    <Row gutter={24}>
      <Col span={24}>
        {/* Yard Name */}
        <Form.Item
          label="Yard Name"
          name="yardName"
          rules={[{ required: true, message: "Yard name is required" }]}
        >
          <Input placeholder="Enter yard name" size="large" />
        </Form.Item>

      <Form.List name="contacts">
  {(fields, { add, remove }) => (
    <>
      {fields.map((field, index) => (
        <Card
          key={field.key}
          title={index === 0 ? "Main Contact Person" : `Additional Person ${index}`}
          size="small"
          style={{ 
            marginBottom: 16,
            background: index === 0 ? "#fafafa" : "#ffffff",
            border: index === 0 ? "1px solid #e8e8e8" : "1px dashed #1677ff"
          }}
          extra={
            index > 0 && (
              <Button 
                type="text" 
                danger 
                icon={<MinusCircleOutlined />} 
                onClick={() => remove(field.name)}
              />
            )
          }
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* Contact Person Name */}
            <Form.Item
              {...field}
              name={[field.name, "name"]}
              label="Contact Person"
              rules={[{ required: true, message: "Contact person is required" }]}
            >
              <Input 
                placeholder="Enter contact person name"
                prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
              />
            </Form.Item>

            {/* Email */}
            <Form.Item
              name={[field.name, "email"]}
              label="Email"
              rules={[
                { required: index === 0, message: "Email is required" },
                { type: "email", message: "Invalid email format" }
              ]}
            >
              <Input 
                placeholder="Enter email address"
                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
              />
            </Form.Item>

            {/* Phone Number */}
            <Form.Item
              name={[field.name, "phone"]}
              label="Contact Number"
              rules={[
                { required: index === 0, message: "Phone number is required" },
                { 
                  validator: (_, value) => {
                    if (!value && index === 0) return Promise.reject("Phone number is required");
                    if (!value) return Promise.resolve();
                    const cleaned = value.replace(/\D/g, "");
                    if (cleaned.length !== 10) {
                      return Promise.reject("Enter a valid 10-digit number");
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input 
                placeholder="Enter 10-digit number"
                maxLength={14}
                prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
              />
            </Form.Item>

            {/* ADD EXTENSION FIELD HERE */}
            <Form.Item
              name={[field.name, "extension"]}
              label="Extension (Optional)"
              tooltip="Extension number for this contact person"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const extensionRegex = /^[a-zA-Z0-9\-_]+$/;
                    if (!extensionRegex.test(value)) {
                      return Promise.reject("Extension can only contain letters, numbers, hyphens, and underscores");
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input 
                placeholder="Enter extension (e.g., ext101, 123)"
                maxLength={20}
                prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
              />
            </Form.Item>
          </Space>
        </Card>
      ))}

      {fields.length < 3 && (
        <Button
          type="dashed"
          onClick={() => add()}
          block
          icon={<PlusOutlined />}
          style={{ marginTop: 8 }}
        >
          Add Person (Max 3 Total)
        </Button>
      )}
    </>
  )}
</Form.List>

        {/* Website */}
        <Form.Item label="Website" name="link" style={{ marginTop: 16 }}>
          <Input 
            placeholder="Enter website URL (e.g., https://example.com)"
            prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
          />
        </Form.Item>
      </Col>
    </Row>

    {/* Address Information Section */}
    <Divider orientation="left">Address Information</Divider>
    
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Address"
          name={["yardAddress", "address"]}
          rules={[{ required: true, message: "Address is required" }]}
        >
          <Input.TextArea 
            placeholder="Enter full address" 
            rows={2}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item label="Street" name={["yardAddress", "street"]}>
          <Input placeholder="Street name (optional)" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          label="City"
          name={["yardAddress", "city"]}
        >
          <Input placeholder="City name" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          label="State"
          name={["yardAddress", "state"]}
        >
          <Input placeholder="State" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          label="Zip Code"
          name={["yardAddress", "zip_code"]}
          rules={[
            { required: true, message: "Zip code is required" },
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: "Enter a valid zip code (12345 or 12345-6789)",
            },
          ]}
        >
          <Input placeholder="Postal code" maxLength={10} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item label="Country" name={["yardAddress", "country"]}>
          <Input placeholder="Country" disabled />
        </Form.Item>
      </Col>
    </Row>
  </Form>
</Modal>
    </div>
  );
};

export default YardDetailsPage;
